import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { useAbility } from '@casl/react';

import {
  deleteUnjustifiedAbsenceRequest,
  exportUnjustifiedAbsencesRequest,
  importTemplateUnjustifiedAbsenceRequest,
  importUnjustifiedAbsenceRequest,
  preImportUnjustifiedAbsenceRequest,
  debounceIndexUnjustifiedAbsencesRequest
} from '../../requests/unjustifiedAbsences';
import { AbilityContext } from '../../config/abilityContext';
import { sendAlert } from '../../actions/utils';
import defaultColumns from './columns';
import {
  ComponentDataTable,
  DefaultModal,
  SimpleCenteredModal,
  ButtonTooltip,
  Icon,
  ImportModal
} from '../../components';
import { downloadFile } from '../../services/utils';
import UnjustifiedAbsenceShow from './UnjustifiedAbsenceShow';

const UnjustifiedAbsenceDataTable = ({
  columns,
  customParams,
  moreData,
  setMoreData,
  reloadData,
  searchFirstStart,
  defaultStartDate = null,
  defaultEndDate = null
}) => {
  const ability = useAbility(AbilityContext);
  const [unjustifiedAbsence, setUnjustifiedAbsences] = useState([]);
  const [amount, setAmount] = useState(0);
  const [modalBody, setModalBody] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalAction, setModalAction] = useState(() => null);
  const [modalItem, setModalItem] = useState({});
  const [modalShow, setModalShow] = useState(false);
  const [query, setQuery] = useState({});
  const [simpleModalBody, setSimpleModalBody] = useState('');
  const [simpleModalShow, setSimpleModalShow] = useState(false);
  const [onRequest, setOnRequest] = useState(true);
  const isInitialMount = useRef(true);
  const dispatch = useDispatch();
  const tableColumns = columns || defaultColumns;

  const handleModalClose = () => {
    setModalShow(false);
    setModalBody('');
    setModalItem({});
    setSimpleModalShow(false);
    setSimpleModalBody('');
    setModalTitle('');
  };

  const handleSuccessAction = message => {
    dispatch(sendAlert({ kind: 'success', message }));
    handleModalClose();
    setMoreData(!moreData);
  };

  const handleSuccessIndex = response => {
    const { amount: rAmount } = response.data.metadata;
    setUnjustifiedAbsences(response.data.data);
    setAmount(rAmount);
    setOnRequest(false);
  };

  const handleFailureRequest = error => {
    dispatch(sendAlert({ kind: 'error', message: error.response?.data?.message }));
    setOnRequest(false);
  };

  const handleRequest = params => {
    setOnRequest(true);
    setQuery({ ...customParams, ...params });
    const request = async () =>
      debounceIndexUnjustifiedAbsencesRequest({
        dispatch,
        params: {
          ...customParams,
          ...params
        },
        successCallback: handleSuccessIndex
      });
    request();
  };

  const removeUnjustifiedAbsence = item => {
    deleteUnjustifiedAbsenceRequest(item.id, {
      dispatch,
      successCallback: () => handleSuccessAction('Ausencia eliminada con éxito'),
      failureCallback: handleFailureRequest
    });
  };

  const setModal = (title, body) => {
    setModalTitle(title);
    setSimpleModalBody(body);
    setSimpleModalShow(true);
  };

  const downloadUnjustifiedAbsence = item => {
    if (item?.file_info?.file_url) {
      window.open(item.file_info.file_url, '_blank');
    } else {
      dispatch(sendAlert({ kind: 'error', message: 'No hay archivo adjunto disponible para descargar.' }));
    }
    setSimpleModalShow(false);
  };

  const handleButtonClick = (item, action) => {
    switch (action) {
      case 'show':
        setModal(
          `Ausencia - ${item.employee.full_name}`,
          <UnjustifiedAbsenceShow unjustifiedAbsence={camelCaseRecursive(item)} onHide={handleModalClose} />
        );
        break;
      case 'destroy':
        setModalItem(item);
        setModalBody(`¿Estás seguro que deseas eliminar esta Ausencia de ${item.employee.full_name}?`);
        setModalTitle('Eliminar Ausencia');
        setModalAction(() => removeUnjustifiedAbsence);
        setModalShow(true);
        break;
      case 'download':
        setModalItem(item);
        setModalTitle('Descargar archivo');
        setModalBody('¿Estás seguro que deseas descargar el archivo adjunto de esta ausencia?');
        setModalAction(() => downloadUnjustifiedAbsence);
        setModalShow(true);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };

  const handleImportModalExceptions = error => {
    if (error?.response?.status === 422) {
      const alertInfo = <pre>{error?.response?.data?.message}</pre>;
      setModalTitle('Información Relevante');
      setModalShow(true);
      setModalBody(alertInfo);
      setModalAction(() => handleModalClose);
    } else {
      dispatch(sendAlert({ kind: 'error', message: error?.response?.data?.message }));
    }
  };

  const handleDownload = () => {
    setOnRequest(true);

    exportUnjustifiedAbsencesRequest({
      dispatch,
      params: query,
      successCallback: response => {
        setModalShow(false);
        downloadFile(response);
        setOnRequest(false);
      },
      failureCallback: handleFailureRequest
    });
  };

  const downloadModal = () => {
    setModalTitle('Exportar Ausencias Injustificadas');
    setModalShow(true);
    setModalBody('¿Estás seguro que deseas descargar el Ausencias Injustificadas?');
    setModalAction(() => handleDownload);
  };

  const handleMassAction = action => {
    switch (action) {
      case 'import':
        setModalTitle('Importar Ausencias Injustificadas');
        setSimpleModalBody(
          <ImportModal
            onDropUploaded={preImportUnjustifiedAbsenceRequest}
            handleTemplate={importTemplateUnjustifiedAbsenceRequest}
            onHide={importUnjustifiedAbsenceRequest}
            hideModal={() => setSimpleModalShow(false)}
            updateData={() => setMoreData(!moreData)}
            handleExceptions={handleImportModalExceptions}
          />
        );
        setSimpleModalShow(true);
        break;
      case 'export':
        downloadModal();
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };

  const sortColumnCase = name => {
    switch (name) {
      case 'employee_name':
        return { sort_employees: 'employee' };
      case 'employee_job_management':
        return { sort_job_managements: name };
      case 'employee_job_title':
        return { sort_job_titles: name };
      default:
        return { sort_column: name };
    }
  };

  const handleOnUpdate = () => {
    if (isInitialMount.current) isInitialMount.current = false;
    else handleRequest();
  };

  useEffect(handleOnUpdate, [reloadData]);

  return (
    <>
      <div className="data-table-yellow">
        <ComponentDataTable
          onRequest={onRequest}
          columns={tableColumns(handleButtonClick)}
          data={unjustifiedAbsence}
          totalRows={amount}
          handleSortCase={sortColumnCase}
          moreData={moreData}
          resourceRequest={handleRequest}
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
          rangePicker
          preName="unjustified_absence"
          onRowClicked={item => handleButtonClick(item, 'show')}
          pointerOnHover
          withMassActions
          massActions={
            <>
              {ability.can('export', 'UnjustifiedAbsence') && (
                <ButtonTooltip
                  variant="circle-primary"
                  className="mr-2 btn-circle mb-3"
                  text="Exportar"
                  onClick={() => handleMassAction('export')}
                >
                  <Icon icon="download-2" />
                </ButtonTooltip>
              )}
              ,
              {ability.can('import', 'UnjustifiedAbsence') && (
                <ButtonTooltip
                  variant="circle-primary"
                  className="mr-2 btn-circle mb-3"
                  text="Importar"
                  onClick={() => handleMassAction('import')}
                >
                  <Icon icon="cloud-upload-outline" />
                </ButtonTooltip>
              )}
            </>
          }
          searchFirstStart={searchFirstStart}
        />
        <DefaultModal
          title={modalTitle}
          body={modalBody}
          show={modalShow}
          handleClose={handleModalClose}
          handleConfirm={() => modalAction(modalItem)}
          titleBtnClose="Cancelar"
          titleBtnSave="Confirmar"
        />
      </div>
      <SimpleCenteredModal title={modalTitle} body={simpleModalBody} show={simpleModalShow} onHide={handleModalClose} />
    </>
  );
};

export default UnjustifiedAbsenceDataTable;
